/*eslint-disable */
import {
  DivIcon,
  GeoJSON, LatLng, LayerGroup, LeafletEvent, Marker,
} from 'leaflet';
import IFeature from '@/interfaces/IFeature';
import { center } from '@turf/turf';
import IPosition from '@/interfaces/IPosition';
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax,import/no-extraneous-dependencies
import { PruneCluster, PruneClusterForLeaflet } from 'exports-loader?PruneCluster,PruneClusterForLeaflet!prunecluster/dist/PruneCluster.js';
import { IFeatureCollection } from '@/interfaces/IFeatureCollection';
import { IParkingData } from '@/interfaces/IParkingData';

export default abstract class LayerService {
  protected clusteringConstant = 60;

  abstract redrawLayer(): LayerGroup;

  public noData: string = 'b/d';

  // eslint-disable-next-line global-require
  protected markerIcon = 'obszary-parkingowe.svg';

  /**
   * Creates a prune cluster marker.
   * @param {IPosition} position Position at which the marker will be set.
   * @param {IFeature} feature object with metadata for the marker
   */
  public createPruneClusterMarker =
    (position: IPosition, feature: IFeature): PruneCluster.Marker => {
      const positionMarker: PruneCluster.Marker =
        new PruneCluster.Marker(position.lat, position.lng) as PruneCluster.Marker;
      positionMarker.data.metaData = feature.properties;

      return positionMarker;
    };

  /**
   * Handles a click on a map object
   * @param e {LeafletEvent} event object
   * @param metaData {any} object that has metadata
   * @param leafletMarker {Marker} leaflet marker for the object itself or it's center
   */
  abstract onClick(e: LeafletEvent, metaData: any, leafletMarker: Marker): void;

  /**
   * Generates a {LayerGroup} filled with markers element from a GeoJSON position collection
   * @param positions {IFeatureCollection} list of GeoJSON positions to be drawn on the map
   */
  protected generatePruneClusterMarkerLayer = (positions: IFeatureCollection): LayerGroup => {
    const pruneCluster = new PruneClusterForLeaflet(this.clusteringConstant);

    // WARN: the code inside this function isn't type safe because the plugin
    // doesn't have popper type definitions.
    pruneCluster.PrepareLeafletMarker = (leafletMarker: Marker, data: any): void => {
      const metaData = data.metaData as any;
      leafletMarker.setIcon(new DivIcon({
        html: this.generateHtmlPointTemplate(metaData, leafletMarker),
        iconSize: [25, 25],
        iconAnchor: [25 / 2, 25 / 2],
        popupAnchor: [0, 0],
      }));
      // events have bound itself multiple times
      // during redraw so it's safer to remove them
      leafletMarker.off('click');
      leafletMarker.on('click', (e) => {
        this.onClick(e, metaData, leafletMarker);
      });
    };

    positions.features.map((feature: IFeature): PruneCluster.Marker => {
      if (!feature.geometry) {
        return new PruneCluster.Marker();
      }
      const { coordinates } = feature.geometry;
      const [lng, lat] = coordinates as Array<number>;
      const marker: PruneCluster.Marker =
        this.createPruneClusterMarker({ lat, lng }, feature);

      pruneCluster.RegisterMarker(marker);
      return marker;
    });

    return pruneCluster;
  };

  /**
   * Generates a {LayerGroup} filled with polygons from elements from a GeoJSON position collection.
   * @param polygons {IFeatureCollection} GeoJSON FeatureCollection
   */
  protected generatePolygonsLayer = (polygons: IFeatureCollection): LayerGroup => {
    const layerGroup = new LayerGroup();

    polygons.features.map((feature: IFeature): GeoJSON => {
      const [lat, lng] = this.findPolygonCenter(feature);
      const metaData = feature.properties as any;
      // any used because IFeature definition isn't exactly the same as leaflets
      // internal GeoJSON feature definition
      const polygon: GeoJSON = new GeoJSON(feature as any, {
        coordsToLatLng: this.coordsToLatLng,
      });

      // Set polygon color
      polygon.setStyle({
        color: this.setColor(feature),
      });

      polygon.on('click', (e) => {
        this.onClick(e, metaData, new Marker(new LatLng(lat, lng)));
      });

      layerGroup.addLayer(polygon);
      return polygon;
    });
    return layerGroup;
  };

  /**
   * Set Color for different type and spots
   *
   * @param feature
   */
  protected setColor(feature: IFeature): string {
    const color = feature.properties.color;
    if (!!color) {
      return color;
    }

    if (feature.geometry.type === 'MultiLineString' && feature.properties.last_status) {
      let color = '';

      const occupiedPercent = +((100 * feature.properties.last_status.occupiedSpots) / parseInt(feature.properties.parkingSpots)).toFixed(0);
      if (occupiedPercent < 50) {
        color = '#28972f';
      } else if (occupiedPercent >= 50 && occupiedPercent <= 80) {
        color = '#FFE11B';
      } else if (occupiedPercent > 80) {
        color = '#de3e3e';
      } else {
        color = '#de3e3e';
      }

      return color;
    }

    return '#de3e3e';
  }

  /**
   * Generates the content for a {DivIcon} that will be put on map.
   * @param metaData {any} Metadata about the marker object (content of {IFeature} properties)
   * @param leafletMarker {Marker} Actual marker object
   */
  protected generateHtmlPointTemplate = (metaData: any, leafletMarker: Marker) => {
    const templateData: IParkingData = metaData as IParkingData;
    templateData.navigationLatLng = leafletMarker.getLatLng();
    const template = `
        <div class="map-custom-icon"
        style='background-image: url("${require('@/assets/img/map-icons/' + this.markerIcon)}")'>
        <div>
      `;

    return template;
  };

  /**
   * Returns an [lat, lng] array with the center of GeoJSON object.
   * @param polygonData GeoJSON object that is which center is going to be found.
   */
  protected findPolygonCenter =
    (polygonData: IFeature): number[] => {
      const polygon: GeoJSON = new GeoJSON(polygonData as any, {
        coordsToLatLng: this.coordsToLatLng,
      });
      const centroidPoint: any = center(polygon.toGeoJSON() as any);
      return [
        centroidPoint.geometry.coordinates[1],
        centroidPoint.geometry.coordinates[0],
      ];
    };

  /**
   * LatLng conversion method for use with Leaflet GeoJson constructor
   * @param coords input coordinates
   */
  protected coordsToLatLng = (coords: [number, number] | [number, number, number]): LatLng => {
    if (coords.length > 3) {
      return new LatLng(coords[1], coords[0]);
    }
    return new LatLng(coords[1], coords[0], coords[2]);
  };

  /**
   * Merge object with the same property
   * @param array
   * @param property
   */
  protected mergeObjectsInUnique = (items: any[], property: string): [] => {
    const newArray = new Map();

    items.forEach((item: any) => {
      const propertyValue = item[property];
      if (newArray.has(propertyValue)) {
        newArray.set(propertyValue, { ...item, ...newArray.get(propertyValue) });
      } else {
        newArray.set(propertyValue, item);
      }
    });

    return [...newArray.values()] as [];
  }

  /**
   * Sum values in array by key name
   * @param items
   * @param rowCountName
   */
  sumParkingCategoryPlaces = (items: [], rowCountName: any):number => {
    const sum = items
      .map(item => item[rowCountName])
      .reduce((prev, curr) => prev + curr, 0);
    return sum;
  };
}
